import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  Typography,
} from "@mui/material";
import { Calendar } from "react-date-range";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";
import { DatePickerIcon } from "../../../components/Icons/LivisIcons"; // adjust path as needed
import { useTranslation } from "react-i18next";

// react-date-range CSS — import once at app root if not already imported
// import "react-date-range/dist/styles.css";
// import "react-date-range/dist/theme/default.css";

interface LivisCalendarPickerProps {
  label?: string;
  value: Dayjs | null;
  onChange: (val: Dayjs | null) => void;
  /** Earliest selectable date */
  minDate?: Date;
  /** Latest selectable date (defaults to today) */
  maxDate?: Date;
  placeholder?: string;
  /** Extra sx forwarded to the wrapping FormControl box */
  sx?: object;
  /** Standard variant forwarded to TextField */
  variant?: "standard" | "outlined" | "filled";
  className?: string;
  /** When true, applies the app's standard red error styling */
  error?: boolean;
  /** Message shown below the field when error is true */
  helperText?: string;
}

const LivisCalendarPicker: React.FC<LivisCalendarPickerProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  placeholder = "Select date",
  sx,
  variant = "standard",
  className,
  error = false,
  helperText,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);

  // Internal date for react-date-range (needs a real Date, not null)
  const internalDate: Date = value ? value.toDate() : new Date();

  const displayValue = value ? value.format("MM/DD/YYYY") : "";

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const close = () => setAnchorEl(null);

  const handleSelect = (date: Date) => {
    onChange(dayjs(date));
    close();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Box sx={{ position: "relative", ...sx }} className={className}>
      {/* Trigger input */}
      <TextField
        ref={inputRef}
        label={label ? t(label) : undefined}
        value={displayValue}
        placeholder={t(placeholder) || ""}
        onClick={open}
      //  readOnly
        fullWidth
        variant={variant}
        autoComplete="off"
        className={`livis-input-field livis-calendar-picker-input${error ? " livis-input-field-error" : ""}`}
        error={error}
        InputLabelProps={{ className: "livis-input-field-component-label", shrink: true }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              {value ? (
                <IconButton
                  size="small"
                  onClick={handleClear}
                  aria-label="Clear date"
                  edge="end"
                  sx={{ mr: -0.5 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : (
                <IconButton
                  size="small"
                  onClick={open}
                  aria-label="Open calendar"
                  edge="end"
                  sx={{ mr: -0.5 }}
                >
                  <DatePickerIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{ cursor: "pointer", "& input": { cursor: "pointer" } }}
      />
      {helperText && (
        <small className="livis-text-danger" style={{ display: "block" }}>
          {helperText}
        </small>
      )}

      {/* Calendar Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
            overflow: "hidden",
            mt: 0.5,
          },
        }}
      >
        <Box sx={{ p: 0 }}>
          {/* Header showing selected date */}
          {value && (
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#0f172a" }}
              >
                {value.format("ddd, MMM D, YYYY")}
              </Typography>
              <IconButton size="small" onClick={handleClear} sx={{ ml: 1 }}>
                <CloseIcon fontSize="small" sx={{ color: "#64748b" }} />
              </IconButton>
            </Box>
          )}

          {/* react-date-range Calendar — single date, no range */}
          <Calendar
            date={internalDate}
            onChange={handleSelect}
            minDate={minDate}
            maxDate={maxDate || new Date()}
            showPreview={false}
            showDateDisplay={false}
            showMonthAndYearPickers
            // Inherit app colors via CSS variable override below
            color="var(--livis-accent-color, #2563eb)"
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default LivisCalendarPicker;